import React from 'react'
import isNil from 'lodash/isNil'
import toNumber from 'lodash/toNumber'
import type { InlineInputProps } from '@ztechapps/webui.input'
import { InlineInput, NumberInput, InlineDisplayEmptyContent } from '@ztechapps/webui.input'

export type ConversionFactorInlineInputProps = InlineInputProps & {
  calculatedFactor: number | null
}

/**
 * Custom inline input for conversion factor that:
 * 1. Shows calculated factor as default when no manual value is set
 * 2. Uses calculated factor when saving if no manual value provided
 */
export const ConversionFactorInlineInput = ({
  parentInputComponent,
  name,
  inputProps,
  updateParams,
  displayContent,
  calculatedFactor,
  initialValues,
  ...rest
}: ConversionFactorInlineInputProps) => {
  const ParentInput = parentInputComponent || InlineInput

  // Display: show stored value if exists, otherwise show calculated factor
  const effectiveDisplayContent =
    displayContent ??
    ((value) => {
      if (!isNil(value)) return `${value}`
      if (calculatedFactor != null) return <span className="text-muted">{calculatedFactor}</span>
      return <InlineDisplayEmptyContent />
    })

  // Update params: use the entered value, or fall back to calculated factor if empty
  const effectiveUpdateParams =
    updateParams ??
    (({ updatedValue, _id }) => {
      const numValue = toNumber(updatedValue)
      // If user cleared the value or it's NaN, use calculated factor
      const finalValue = isNaN(numValue) || updatedValue === '' || updatedValue === null ? calculatedFactor : numValue

      return {
        input: {
          _id,
          [name]: finalValue,
        },
      }
    })

  return (
    <ParentInput
      {...{
        ...rest,
        name,
        initialValues,
        updateParams: effectiveUpdateParams,
        displayContent: effectiveDisplayContent,
        editContent: (props) => (
          <NumberInput
            {...{
              ...props,
              ...inputProps,
              placeholder: calculatedFactor != null ? String(calculatedFactor) : '',
            }}
          />
        ),
      }}
    />
  )
}
